import { FeeScheduleGroup, RateInput } from '@bariendo/lib/receivables';
import { Box, Loader } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNow } from '@/hooks/useNow';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { alert } from '@/services/alert';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { shortName } from '../imports/manualEntry';
import { formatRateDate, RateGroup, rateSeries } from '../rates/rateSchedule';
import { RateScheduleTable } from '../rates/RateScheduleTable';
import { RatesDrawer, RatesDrawerSave } from '../rates/RatesDrawer';
import { FEE_PROCEDURES, feeGroupKey, feeGroupName } from './feeSchedule';
import { useFeeSchedule } from './useFeeSchedule';

/** Receivables → Fee schedule (BAR-3039): what each payor pays us per procedure. */

const failure = (error: unknown) => (error instanceof Error ? error.message : 'unknown error');

export function FeeScheduleView({ now }: { now?: Date }): ReactElement {
  const medplum = useMedplum();
  const resolvedNow = useNow(now);
  const today = dayjs(resolvedNow).format('YYYY-MM-DD');
  const schedule = useFeeSchedule();
  const { practitioners } = useProviderDirectory();
  const [drawer, setDrawer] = useState<{ key: string; mode: 'update' | 'correct'; opened: number }>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (schedule.error) alert.error(`Couldn't load the fee schedule: ${schedule.error}`);
  }, [schedule.error]);

  const groups = useMemo(
    (): (RateGroup & { source: FeeScheduleGroup })[] =>
      schedule.groups.map((group) => ({
        key: feeGroupKey(group),
        name: feeGroupName(group),
        versions: group.rates,
        source: group,
      })),
    [schedule.groups]
  );
  const open = groups.find((group) => group.key === drawer?.key);

  const whoName = (reference: string): string => {
    const practitioner = practitioners.find((candidate) => getReferenceString(candidate) === reference);
    return practitioner ? shortName(practitioner) : '—';
  };
  const procedureLabel = (procedure: string) =>
    FEE_PROCEDURES.find((option) => option.value === procedure)?.label ?? procedure;

  // A failed write re-reads the schedule: one that timed out may still have landed (BAR-3125).
  const call = <T,>(input: Record<string, unknown>) =>
    fetchBotWrite<T>(medplum, 'create-treatment-payment', input, schedule.reload);

  const save = async (change: RatesDrawerSave): Promise<void> => {
    if (!open) return;
    const group = { billTo: open.source.billTo, location: open.source.location };
    setSaving(true);
    try {
      if (change.mode === 'update') {
        const result = await call<{ scheduled: number; replacedScheduled: boolean }>({
          operation: 'updateRates',
          feeScheduleGroup: group,
          effectiveFrom: change.effectiveFrom,
          rates: change.rates,
        });
        showUndoToast(
          `Rates updated · ${open.name} · from ${formatRateDate(change.effectiveFrom)}`,
          // An update that replaced an earlier scheduled one can't bring it back.
          result.scheduled > 0 && !result.replacedScheduled
            ? () =>
                call({ operation: 'removeScheduledRates', feeScheduleGroup: group })
                  .then(schedule.reload)
                  .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
            : undefined
        );
      } else {
        const { correctedIds } = await call<{ correctedIds: string[] }>({
          operation: 'correctRates',
          feeScheduleGroup: group,
          rates: change.rates,
          reason: change.reason,
        });
        showUndoToast(`Corrected · ${open.name}`, () =>
          call({ operation: 'undoRateCorrection', rateIds: correctedIds })
            .then(schedule.reload)
            .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
        );
      }
      setDrawer(undefined);
      schedule.reload();
    } catch (error) {
      alert.error(`Couldn't save the rates: ${failure(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const removeScheduled = async (): Promise<void> => {
    if (!open) return;
    const group = { billTo: open.source.billTo, location: open.source.location };
    try {
      const removed = await call<{ effectiveFrom: string | null; rates: RateInput[] }>({
        operation: 'removeScheduledRates',
        feeScheduleGroup: group,
      });
      setDrawer(undefined);
      schedule.reload();
      if (removed.effectiveFrom) {
        const from = removed.effectiveFrom;
        showUndoToast(`Update from ${formatRateDate(from)} removed · ${open.name}`, () =>
          call({ operation: 'updateRates', feeScheduleGroup: group, effectiveFrom: from, rates: removed.rates })
            .then(schedule.reload)
            .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
        );
      }
    } catch (error) {
      alert.error(`Couldn't remove the scheduled update: ${failure(error)}`);
    }
  };

  if (schedule.loading && groups.length === 0) {
    return (
      <Box p={16}>
        <Loader size="sm" />
      </Box>
    );
  }

  if (schedule.error && groups.length === 0) {
    return <LoadFailed what="the fee schedule" error={schedule.error} onRetry={schedule.reload} />;
  }

  return (
    <Box style={{ flex: 1, overflow: 'auto' }}>
      <RateScheduleTable
        groups={groups}
        today={today}
        procedureLabel={procedureLabel}
        whoName={whoName}
        onUpdate={(key) => setDrawer({ key, mode: 'update', opened: Date.now() })}
        onCorrect={(key) => setDrawer({ key, mode: 'correct', opened: Date.now() })}
      />
      {open && drawer && (
        <RatesDrawer
          // A fresh draft each time the drawer opens.
          key={drawer.opened}
          opened
          mode={drawer.mode}
          groupName={open.name}
          series={rateSeries(open.versions, today)}
          procedures={FEE_PROCEDURES}
          today={today}
          saving={saving}
          onClose={() => setDrawer(undefined)}
          onSave={(change) => void save(change)}
          onRemoveScheduled={() => void removeScheduled()}
        />
      )}
    </Box>
  );
}
